import { useEffect } from 'react';
import shallow from 'zustand/shallow';
import {
  useToast,
  Box,
  Modal,
  Grid,
  GridItem,
  ModalBody,
  ModalOverlay,
  Text,
  ModalContent,
  ModalCloseButton,
  FormLabel,
  Flex,
  Button,
} from '@chakra-ui/react';
import { Formik, Form, Field } from 'formik';

import {
  ACADEMIC_YEAR_SCHEMA,
  YEARS_OPTION,
  SEMESTER_OPTION,
} from '../constants';
import { useUpdateAcademicYear } from '../context/hooks';
import { useAcademicYear } from '../context';
import { TextField, Select } from '../../../../lib/components';
import { formatIntlDate } from '../../../../lib/helper';

function UpdateAcademicYear({ isOpen, onClose }) {
  const [selected] = useAcademicYear((state) => [state.selected], shallow);

  const [update, status, errorMessage, fieldErrors] = useUpdateAcademicYear();

  const toast = useToast();

  useEffect(() => {
    if (status === 'resolved') {
      toast({
        title: 'Sukses',
        description: 'Berhasil Update Tahun Ajaran',
        status: 'success',
        isClosable: true,
      });
      onClose();
    } else if (status === 'rejected') {
      toast({
        title: 'Kesalahan',
        description: errorMessage,
        status: 'error',
        isClosable: true,
      });
    }
  }, [status]);

  return (
    <Modal isOpen={isOpen} onClose={status != 'fetching' && onClose} size="4xl">
      <ModalOverlay />
      <ModalContent>
        <Text mt={3} ml={6}>
          Edit Tahun Ajaran
        </Text>
        <ModalCloseButton />
        <ModalBody>
          <Box bgColor="blue.50" mt={2} mb={6} p={6} borderRadius={5}>
            <Formik
              initialValues={{
                years: selected?.years,
                semester: selected?.semester,
                startSemester: formatIntlDate(selected?.startSemester),
                endSemester: formatIntlDate(selected?.endSemester),
                description: selected?.description,
              }}
              validationSchema={ACADEMIC_YEAR_SCHEMA}
              onSubmit={(values) => {
                const payload = {
                  id: selected?.id,
                  years: values.years,
                  semester: values.semester,
                  startSemester: formatIntlDate(values.startSemester),
                  endSemester: formatIntlDate(values.endSemester),
                  description: values.description,
                };
                update(payload);
              }}
            >
              {() => (
                <Form>
                  <Grid
                    templateColumns="repeat(8, 1fr)"
                    justifyContent="space-between"
                    gap={4}
                  >
                    <GridItem colSpan={{ base: 8, md: 2 }}>
                      <FormLabel htmlFor="years">Tahun</FormLabel>
                      <Field name="years">
                        {({ field, form }) => (
                          <Select
                            id="years"
                            placeholder="Pilih Tahun"
                            inputProps={{
                              ...field,
                              size: 'sm',
                              bgColor: 'white',
                            }}
                            errorMessage={
                              form.touched.years &&
                              (form.errors.years || fieldErrors?.years)
                            }
                            options={YEARS_OPTION}
                          />
                        )}
                      </Field>
                    </GridItem>
                    <GridItem colSpan={{ base: 8, md: 2 }}>
                      <FormLabel htmlFor="semester">Semester</FormLabel>
                      <Field name="semester">
                        {({ field, form }) => (
                          <Select
                            id="semester"
                            placeholder="Pilih Tahun"
                            inputProps={{
                              ...field,
                              size: 'sm',
                              bgColor: 'white',
                            }}
                            errorMessage={
                              form.touched.semester &&
                              (form.errors.semester || fieldErrors?.semester)
                            }
                            options={SEMESTER_OPTION}
                          />
                        )}
                      </Field>
                    </GridItem>
                    <GridItem colSpan={{ base: 8, md: 2 }}>
                      <Field name="startSemester">
                        {({ field, form }) => (
                          <TextField
                            id="startSemester"
                            label="Awal Semester"
                            placeholder="Tanggal Awal Semester"
                            type="date"
                            errorMessage={
                              form.touched.startSemester &&
                              (form.errors.startSemester ||
                                fieldErrors?.startSemester)
                            }
                            inputProps={{
                              ...field,
                              size: 'sm',
                              bgColor: 'white',
                            }}
                          />
                        )}
                      </Field>
                    </GridItem>
                    <GridItem colSpan={{ base: 8, md: 2 }}>
                      <Field name="endSemester">
                        {({ field, form }) => (
                          <TextField
                            id="endSemester"
                            label="Akhir Semester"
                            placeholder="Tanggal Akhir Semester"
                            type="date"
                            errorMessage={
                              form.touched.endSemester &&
                              (form.errors.endSemester ||
                                fieldErrors?.endSemester)
                            }
                            inputProps={{
                              ...field,
                              size: 'sm',
                              bgColor: 'white',
                            }}
                          />
                        )}
                      </Field>
                    </GridItem>
                    <GridItem colSpan={{ base: 8, md: 4 }}>
                      <Field name="description">
                        {({ field }) => (
                          <TextField
                            id="description"
                            label="Deskripsi"
                            placeholder="Deskripsi Tahun Ajaran"
                            inputProps={{
                              ...field,
                              size: 'sm',
                              bgColor: 'white',
                            }}
                          />
                        )}
                      </Field>
                    </GridItem>
                    <GridItem colSpan={{ base: 8, md: 4 }}>
                      <Flex justify="end" alignItems="flex-end" height="100%">
                        <Button
                          bgColor="cyan.700"
                          type="submit"
                          color="white"
                          variant="solid"
                          size="sm"
                          isLoading={status == 'fetching'}
                        >
                          Update Tahun Ajaran
                        </Button>
                      </Flex>
                    </GridItem>
                  </Grid>
                </Form>
              )}
            </Formik>
          </Box>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}

export default UpdateAcademicYear;
